// Phase 2: Mock Role Override Service
import type {
  ProjectRoleOverride,
  EmployeeRoleOverride,
  CreateProjectRoleOverrideDto,
  CreateEmployeeRoleOverrideDto,
  UpdateProjectRoleOverrideDto,
  UpdateEmployeeRoleOverrideDto,
  RateResolutionRequest,
  RateResolutionResponse,
  EffectiveRate,
} from '@/types/roleOverride';

// Mock data
let mockProjectRoleOverrides: ProjectRoleOverride[] = [
  {
    id: 1,
    projectId: 1,
    projectName: 'ABC Corp Website Redesign',
    roleId: 1,
    roleName: 'Senior Developer',
    billingRate: 180,
    costRate: 85,
    currency: 'USD',
    effectiveFrom: '2024-01-01T00:00:00Z',
    effectiveTo: undefined,
    reason: 'Premium client - higher billing rate negotiated',
    createdAt: '2024-01-01T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    status: 'Active',
    marginPercentage: 52.8,
  },
  {
    id: 2,
    projectId: 2,
    projectName: 'XYZ Mobile App',
    roleId: 2,
    roleName: 'Junior Developer',
    billingRate: 65,
    costRate: 45,
    currency: 'USD',
    effectiveFrom: '2024-02-01T00:00:00Z',
    effectiveTo: undefined,
    reason: 'Discounted rate for non-profit client',
    createdAt: '2024-02-01T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    status: 'Active',
    marginPercentage: 30.8,
  },
  {
    id: 3,
    projectId: 3,
    projectName: 'Internal Tools Development',
    roleId: 3,
    roleName: 'Project Manager',
    billingRate: 100,
    costRate: 70,
    currency: 'USD',
    effectiveFrom: '2024-01-15T00:00:00Z',
    effectiveTo: '2024-06-30T00:00:00Z',
    reason: 'Internal project - reduced billing rate',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    status: 'Expired',
    marginPercentage: 30.0,
  },
];

let mockEmployeeRoleOverrides: EmployeeRoleOverride[] = [
  {
    id: 1,
    employeeId: 1,
    employeeName: 'John Smith',
    roleId: 1,
    roleName: 'Senior Developer',
    projectId: 1,
    projectName: 'ABC Corp Website Redesign',
    billingRate: 200,
    costRate: 85,
    currency: 'USD',
    effectiveFrom: '2024-01-01T00:00:00Z',
    effectiveTo: undefined,
    reason: 'Top performer - special rate for this client',
    createdAt: '2024-01-01T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    status: 'Active',
    isGlobal: false,
    marginPercentage: 57.5,
  },
  {
    id: 2,
    employeeId: 2,
    employeeName: 'Sarah Johnson',
    roleId: 1,
    roleName: 'Senior Developer',
    projectId: undefined,
    projectName: undefined,
    billingRate: 165,
    costRate: 85,
    currency: 'USD',
    effectiveFrom: '2024-03-01T00:00:00Z',
    effectiveTo: undefined,
    reason: 'Global rate adjustment for senior team member',
    createdAt: '2024-03-01T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    status: 'Active',
    isGlobal: true,
    marginPercentage: 48.5,
  },
  {
    id: 3,
    employeeId: 3,
    employeeName: 'Mike Chen',
    roleId: 2,
    roleName: 'Junior Developer',
    projectId: 4,
    projectName: 'E-commerce Platform',
    billingRate: 95,
    costRate: 45,
    currency: 'USD',
    effectiveFrom: '2024-04-01T00:00:00Z',
    effectiveTo: undefined,
    reason: 'Promoted to lead developer on this project',
    createdAt: '2024-04-01T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    status: 'Active',
    isGlobal: false,
    marginPercentage: 52.6,
  },
  {
    id: 4,
    employeeId: 4,
    employeeName: 'Emily Davis',
    roleId: 3,
    roleName: 'Project Manager',
    projectId: undefined,
    projectName: undefined,
    billingRate: 135,
    costRate: 70,
    currency: 'USD',
    effectiveFrom: '2024-02-15T00:00:00Z',
    effectiveTo: undefined,
    reason: 'Contractor rate - global override',
    createdAt: '2024-02-15T10:00:00Z',
    createdBy: 'admin',
    createdByName: 'Admin User',
    status: 'Active',
    isGlobal: true,
    marginPercentage: 48.1,
  },
];

let nextProjectOverrideId = 4;
let nextEmployeeOverrideId = 5;

// Helper functions
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function calculateMargin(billingRate?: number, costRate?: number): number | undefined {
  if (!billingRate || !costRate || billingRate === 0) return undefined;
  return ((billingRate - costRate) / billingRate) * 100;
}

function determineStatus(effectiveFrom: string, effectiveTo?: string): 'Active' | 'Expired' | 'Future' {
  const now = new Date();
  const from = new Date(effectiveFrom);
  const to = effectiveTo ? new Date(effectiveTo) : null;

  if (from > now) return 'Future';
  if (to && to < now) return 'Expired';
  return 'Active';
}

export const roleOverrideServiceMock = {
  // Project Role Override operations
  async getProjectRoleOverrides(projectId?: number): Promise<ProjectRoleOverride[]> {
    await delay(300);
    if (projectId) {
      return mockProjectRoleOverrides.filter(o => o.projectId === projectId);
    }
    return mockProjectRoleOverrides;
  },

  async getProjectRoleOverrideById(id: number): Promise<ProjectRoleOverride> {
    await delay(300);
    const override = mockProjectRoleOverrides.find(o => o.id === id);
    if (!override) {
      throw new Error(`Project role override with id ${id} not found`);
    }
    return override;
  },

  async createProjectRoleOverride(dto: CreateProjectRoleOverrideDto): Promise<ProjectRoleOverride> {
    await delay(500);
    const now = new Date().toISOString();

    const newOverride: ProjectRoleOverride = {
      id: nextProjectOverrideId++,
      projectId: dto.projectId,
      projectName: `Project ${dto.projectId}`, // In real app, fetch from project service
      roleId: dto.roleId,
      roleName: `Role ${dto.roleId}`, // In real app, fetch from role service
      billingRate: dto.billingRate,
      costRate: dto.costRate,
      currency: dto.currency,
      effectiveFrom: dto.effectiveFrom,
      effectiveTo: dto.effectiveTo,
      reason: dto.reason,
      createdAt: now,
      createdBy: 'current-user',
      createdByName: 'Current User',
      status: determineStatus(dto.effectiveFrom, dto.effectiveTo),
      marginPercentage: calculateMargin(dto.billingRate, dto.costRate),
    };

    mockProjectRoleOverrides.push(newOverride);
    return newOverride;
  },

  async updateProjectRoleOverride(id: number, dto: UpdateProjectRoleOverrideDto): Promise<ProjectRoleOverride> {
    await delay(500);
    const override = mockProjectRoleOverrides.find(o => o.id === id);
    if (!override) {
      throw new Error(`Project role override with id ${id} not found`);
    }

    if (dto.billingRate !== undefined) override.billingRate = dto.billingRate;
    if (dto.costRate !== undefined) override.costRate = dto.costRate;
    if (dto.effectiveTo !== undefined) override.effectiveTo = dto.effectiveTo;
    if (dto.reason !== undefined) override.reason = dto.reason;

    override.status = determineStatus(override.effectiveFrom, override.effectiveTo);
    override.marginPercentage = calculateMargin(override.billingRate, override.costRate);

    return override;
  },

  async deleteProjectRoleOverride(id: number): Promise<void> {
    await delay(500);
    const index = mockProjectRoleOverrides.findIndex(o => o.id === id);
    if (index === -1) {
      throw new Error(`Project role override with id ${id} not found`);
    }
    mockProjectRoleOverrides.splice(index, 1);
  },

  // Employee Role Override operations
  async getEmployeeRoleOverrides(employeeId?: number, projectId?: number): Promise<EmployeeRoleOverride[]> {
    await delay(300);
    let filtered = mockEmployeeRoleOverrides;

    if (employeeId) {
      filtered = filtered.filter(o => o.employeeId === employeeId);
    }

    if (projectId !== undefined) {
      filtered = filtered.filter(o => o.projectId === projectId);
    }

    return filtered;
  },

  async getEmployeeRoleOverrideById(id: number): Promise<EmployeeRoleOverride> {
    await delay(300);
    const override = mockEmployeeRoleOverrides.find(o => o.id === id);
    if (!override) {
      throw new Error(`Employee role override with id ${id} not found`);
    }
    return override;
  },

  async createEmployeeRoleOverride(dto: CreateEmployeeRoleOverrideDto): Promise<EmployeeRoleOverride> {
    await delay(500);
    const now = new Date().toISOString();

    const newOverride: EmployeeRoleOverride = {
      id: nextEmployeeOverrideId++,
      employeeId: dto.employeeId,
      employeeName: `Employee ${dto.employeeId}`, // In real app, fetch from employee service
      roleId: dto.roleId,
      roleName: `Role ${dto.roleId}`, // In real app, fetch from role service
      projectId: dto.projectId,
      projectName: dto.projectId ? `Project ${dto.projectId}` : undefined,
      billingRate: dto.billingRate,
      costRate: dto.costRate,
      currency: dto.currency,
      effectiveFrom: dto.effectiveFrom,
      effectiveTo: dto.effectiveTo,
      reason: dto.reason,
      createdAt: now,
      createdBy: 'current-user',
      createdByName: 'Current User',
      status: determineStatus(dto.effectiveFrom, dto.effectiveTo),
      isGlobal: !dto.projectId,
      marginPercentage: calculateMargin(dto.billingRate, dto.costRate),
    };

    mockEmployeeRoleOverrides.push(newOverride);
    return newOverride;
  },

  async updateEmployeeRoleOverride(id: number, dto: UpdateEmployeeRoleOverrideDto): Promise<EmployeeRoleOverride> {
    await delay(500);
    const override = mockEmployeeRoleOverrides.find(o => o.id === id);
    if (!override) {
      throw new Error(`Employee role override with id ${id} not found`);
    }

    if (dto.billingRate !== undefined) override.billingRate = dto.billingRate;
    if (dto.costRate !== undefined) override.costRate = dto.costRate;
    if (dto.effectiveTo !== undefined) override.effectiveTo = dto.effectiveTo;
    if (dto.reason !== undefined) override.reason = dto.reason;

    override.status = determineStatus(override.effectiveFrom, override.effectiveTo);
    override.marginPercentage = calculateMargin(override.billingRate, override.costRate);

    return override;
  },

  async deleteEmployeeRoleOverride(id: number): Promise<void> {
    await delay(500);
    const index = mockEmployeeRoleOverrides.findIndex(o => o.id === id);
    if (index === -1) {
      throw new Error(`Employee role override with id ${id} not found`);
    }
    mockEmployeeRoleOverrides.splice(index, 1);
  },

  // Rate Resolution
  async resolveRate(request: RateResolutionRequest): Promise<RateResolutionResponse> {
    await delay(300);

    const resolutionPath: string[] = [];

    // Priority 1: Employee + Project Override
    const employeeProjectOverride = mockEmployeeRoleOverrides.find(
      o =>
        o.employeeId === request.employeeId &&
        o.roleId === request.roleId &&
        o.projectId === request.projectId &&
        o.status === 'Active'
    );

    if (employeeProjectOverride) {
      resolutionPath.push('Checked Employee + Project Override: FOUND');
      return {
        effectiveRate: {
          billingRate: employeeProjectOverride.billingRate,
          costRate: employeeProjectOverride.costRate,
          currency: employeeProjectOverride.currency,
          source: 'EmployeeProject',
          sourceId: employeeProjectOverride.id,
          sourceName: `${employeeProjectOverride.employeeName} on ${employeeProjectOverride.projectName}`,
          effectiveFrom: employeeProjectOverride.effectiveFrom,
          marginPercentage: employeeProjectOverride.marginPercentage,
          isOverridden: true,
        },
        appliedOverride: employeeProjectOverride,
        resolutionPath,
      };
    }
    resolutionPath.push('Checked Employee + Project Override: Not found');

    // Priority 2: Employee Global Override
    const employeeGlobalOverride = mockEmployeeRoleOverrides.find(
      o =>
        o.employeeId === request.employeeId &&
        o.roleId === request.roleId &&
        !o.projectId &&
        o.status === 'Active'
    );

    if (employeeGlobalOverride) {
      resolutionPath.push('Checked Employee Global Override: FOUND');
      return {
        effectiveRate: {
          billingRate: employeeGlobalOverride.billingRate,
          costRate: employeeGlobalOverride.costRate,
          currency: employeeGlobalOverride.currency,
          source: 'EmployeeGlobal',
          sourceId: employeeGlobalOverride.id,
          sourceName: `${employeeGlobalOverride.employeeName} (Global)`,
          effectiveFrom: employeeGlobalOverride.effectiveFrom,
          marginPercentage: employeeGlobalOverride.marginPercentage,
          isOverridden: true,
        },
        appliedOverride: employeeGlobalOverride,
        resolutionPath,
      };
    }
    resolutionPath.push('Checked Employee Global Override: Not found');

    // Priority 3: Project + Role Override
    const projectRoleOverride = mockProjectRoleOverrides.find(
      o => o.projectId === request.projectId && o.roleId === request.roleId && o.status === 'Active'
    );

    if (projectRoleOverride) {
      resolutionPath.push('Checked Project + Role Override: FOUND');
      return {
        effectiveRate: {
          billingRate: projectRoleOverride.billingRate,
          costRate: projectRoleOverride.costRate,
          currency: projectRoleOverride.currency,
          source: 'ProjectRole',
          sourceId: projectRoleOverride.id,
          sourceName: `${projectRoleOverride.roleName} on ${projectRoleOverride.projectName}`,
          effectiveFrom: projectRoleOverride.effectiveFrom,
          marginPercentage: projectRoleOverride.marginPercentage,
          isOverridden: true,
        },
        appliedOverride: projectRoleOverride,
        resolutionPath,
      };
    }
    resolutionPath.push('Checked Project + Role Override: Not found');

    // Priority 4: Base Role Rate (from Phase 1)
    resolutionPath.push('Using Base Role Rate');
    return {
      effectiveRate: {
        billingRate: 150, // Mock base rate
        costRate: 85, // Mock base rate
        currency: 'USD',
        source: 'BaseRole',
        sourceId: request.roleId,
        sourceName: 'Senior Developer', // Mock role name
        effectiveFrom: '2024-01-01T00:00:00Z',
        marginPercentage: 43.3,
        isOverridden: false,
      },
      baseRate: {
        billingRate: 150,
        costRate: 85,
        currency: 'USD',
      },
      resolutionPath,
    };
  },
};
